import { HTMLAttributes } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useRouter } from '@tanstack/react-router'
import { cn } from '@/libs/utils'
import { UserServiceUserType } from '@/types/user'
import {
  useAuthServiceKratosEmailLoginMutation,
  useAuthServiceKratosMeLazyQuery,
} from 'zma-react-sdk/auth-service'
import { useAuth } from '@/stores/authStore'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/password-input'

type UserAuthFormProps = HTMLAttributes<HTMLDivElement>

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Please enter your email' })
    .email({ message: 'Invalid email address' }),
  password: z
    .string()
    .min(1, {
      message: 'Please enter your password',
    })
    .min(6, {
      message: 'Password must be at least 6 characters long',
    }),
})

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const auth = useAuth()
  const router = useRouter()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const [getMe] = useAuthServiceKratosMeLazyQuery({
    onCompleted: (data) => {
      if (data?.authServiceKratosMe) {
        auth.setUser({
          id: data.authServiceKratosMe.id,
          email: data.authServiceKratosMe.email || '',
          firstName: data.authServiceKratosMe.firstName || '',
          lastName: data.authServiceKratosMe.lastName || '',
          fullName: `${data.authServiceKratosMe.firstName} ${data.authServiceKratosMe.lastName}`,
          status: data.authServiceKratosMe.status as any,
          type: data.authServiceKratosMe.type as any,
        })
      }
    },
    onError: () => {
      auth.reset()
      toast({
        variant: 'destructive',
        title: 'Session error',
        description: 'Failed to get user information',
      })
    },
  })

  const [loginMutation, { loading: isLoading }] =
    useAuthServiceKratosEmailLoginMutation({
      onCompleted: async (data) => {
        if (data?.authServiceKratosEmailLogin) {
          // Set tokens in auth store
          auth.setTokens(
            data.authServiceKratosEmailLogin.accessToken,
            data.authServiceKratosEmailLogin.refreshToken
          )

          // Call getMe to fetch and set user data
          await getMe()

          toast({
            title: 'Login successful',
            description: 'Welcome back!',
          })

          // Redirect based on user type
          const userType = data.authServiceKratosEmailLogin.user.type
          if (
            userType === UserServiceUserType.Admin ||
            userType === UserServiceUserType.Agency
          ) {
            // Redirect admin and agency users to admin dashboard
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            router.navigate({ to: '/admin/' })
          } else {
            // Redirect regular users to intended page or home
            router.navigate({ to: '/' })
          }
        }
      },
      onError: (error) => {
        toast({
          variant: 'destructive',
          title: 'Login failed',
          description:
            error?.graphQLErrors?.[0]?.message ||
            error?.message ||
            'Invalid email or password',
        })
      },
    })

  function onSubmit(data: z.infer<typeof formSchema>) {
    loginMutation({
      variables: {
        input: {
          email: data.email,
          password: data.password,
        },
      },
    })
  }

  return (
    <div className={cn('my-4 grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder='name@example.com' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <div className='flex items-center justify-between'>
                    <FormLabel>Password</FormLabel>
                    <Link
                      to='/forgot-password'
                      className='text-sm font-medium text-muted-foreground hover:opacity-75'
                      tabIndex={-1}
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <FormControl>
                    <PasswordInput placeholder='********' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className='mt-4' disabled={isLoading}>
              Login
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
